import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum } from "@/lib/format";
import { attT } from "@/lib/attendanceI18n";

/**
 * Employee — daily Check-in / Check-out card. Uses navigator.geolocation.
 * Server enforces geofence (haversine ≤ radius_m).
 */
export default function AttendanceCheckInPage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();

  const [today, setToday] = useState(null);
  const [err, setErr] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setErr("");
    try {
      const { data } = await api.get("/attendance/today");
      setToday(data);
    } catch (e) { setErr(formatApiError(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const doAction = (kind) => {
    if (!navigator.geolocation) { setErr(attT(lang, "ci.gps_unsupported")); return; }
    setBusy(true); setErr(""); setOk("");
    navigator.geolocation.getCurrentPosition(
      async (p) => {
        try {
          const { data } = await api.post(`/attendance/${kind === "check_in" ? "check-in" : "check-out"}`, {
            lat: p.coords.latitude, lng: p.coords.longitude, accuracy_m: p.coords.accuracy,
            device_type: /Mobi/.test(navigator.userAgent) ? "mobile" : "desktop",
          });
          const m = fmtNum(Math.round(data.distance_m));
          setOk(attT(lang, kind === "check_in" ? "ci.ok_in" : "ci.ok_out", { m }));
          await load();
        } catch (e) { setErr(formatApiError(e)); }
        finally { setBusy(false); }
      },
      (e) => { setErr(attT(lang, "ci.gps_fail", { err: e.message })); setBusy(false); },
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
    );
  };

  const css = makeStyles(theme);
  const record = today?.record;
  const checkedIn = !!record?.check_in_at;
  const checkedOut = !!record?.check_out_at;

  return (
    <div dir={dir} style={css.page} data-testid="attendance-check-in-page">
      <div style={css.container}>
        <div style={css.header}>
          <button onClick={() => navigate("/")} style={css.backBtn} data-testid="att-back">
            {attT(lang, dir === "rtl" ? "back" : "back_ltr")}
          </button>
          <h1 style={css.title}>{attT(lang, "title")}</h1>
        </div>

        <div style={css.userBox}>
          <div style={css.label}>{attT(lang, "ci.employee")}</div>
          <div style={css.value}>{user?.full_name || user?.username}</div>
          <div style={css.label}>{attT(lang, "ci.date")}</div>
          <div style={css.value} data-testid="att-today-date">{today?.date}</div>
        </div>

        {err && <div style={css.err} data-testid="att-error">{err}</div>}
        {ok && <div style={css.ok} data-testid="att-ok">{ok}</div>}

        <div style={css.statusGrid}>
          <StatusCard theme={theme} title={attT(lang, "ci.in")} ts={record?.check_in_at} testId="status-check-in" lang={lang} />
          <StatusCard theme={theme} title={attT(lang, "ci.out")} ts={record?.check_out_at} testId="status-check-out" lang={lang} />
        </div>

        <div style={css.actions}>
          <button
            onClick={() => doAction("check_in")}
            disabled={busy || checkedIn}
            style={{ ...css.btnPrimary, opacity: checkedIn ? 0.5 : 1 }}
            data-testid="btn-check-in"
          >
            {busy ? attT(lang, "ci.btn.locating") : checkedIn ? attT(lang, "ci.btn.in_done") : attT(lang, "ci.btn.in")}
          </button>
          <button
            onClick={() => doAction("check_out")}
            disabled={busy || !checkedIn || checkedOut}
            style={{ ...css.btn, opacity: (!checkedIn || checkedOut) ? 0.5 : 1 }}
            data-testid="btn-check-out"
          >
            {checkedOut ? attT(lang, "ci.btn.out_done") : attT(lang, "ci.btn.out")}
          </button>
        </div>

        <div style={css.note}>
          {attT(lang, "ci.note")}
          <br />
          {attT(lang, "ci.hint")}
        </div>
      </div>
    </div>
  );
}

function StatusCard({ title, ts, testId, theme, lang }) {
  return (
    <div style={{
      background: theme.surfaceCard, border: `1px solid ${ts ? theme.gold : theme.border}`,
      borderRadius: 12, padding: 16, textAlign: "center",
    }} data-testid={testId}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 6 }}>{title}</div>
      <div style={{ fontSize: 18, fontWeight: 800, color: ts ? theme.gold : theme.textMuted }}>
        {ts ? new Date(ts).toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit", hour12: false, timeZone: "Asia/Riyadh" }) : "—"}
      </div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: { minHeight: "100vh", background: theme.bg, color: theme.text, fontFamily: "'Tajawal','Segoe UI', sans-serif" },
  container: { maxWidth: 560, margin: "0 auto", padding: "24px 16px" },
  header: { display: "flex", gap: 16, alignItems: "center", marginBottom: 20 },
  backBtn: { background: "transparent", color: theme.text, border: `1px solid ${theme.border}`, padding: "8px 14px", borderRadius: 8, cursor: "pointer" },
  title: { margin: 0, fontSize: 22, fontWeight: 800, color: theme.gold },
  userBox: { background: theme.surfaceCard, border: `1px solid ${theme.borderAccent}`, borderRadius: 12, padding: 14, display: "grid", gridTemplateColumns: "auto 1fr", gap: "6px 14px", marginBottom: 16, fontSize: 13 },
  label: { color: theme.textMuted },
  value: { fontWeight: 700 },
  err: { background: theme.errorBg, color: theme.errorText, padding: 12, borderRadius: 8, marginBottom: 12 },
  ok: { background: "rgba(48,164,108,0.15)", color: "#30a46c", padding: 12, borderRadius: 8, marginBottom: 12 },
  statusGrid: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 },
  actions: { display: "flex", flexDirection: "column", gap: 10, marginBottom: 16 },
  btnPrimary: { background: theme.gold, color: "#000", border: "none", padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 800, fontSize: 16 },
  btn: { background: theme.surfaceCard, color: theme.text, border: `1px solid ${theme.border}`, padding: "14px 20px", borderRadius: 12, cursor: "pointer", fontWeight: 700, fontSize: 15 },
  note: { background: theme.surfaceAlt, border: `1px dashed ${theme.border}`, borderRadius: 8, padding: 12, fontSize: 12, color: theme.textMuted, lineHeight: 1.7 },
});
